import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Form, Input, InputNumber, Select, Space, Typography, Divider, message, Popconfirm } from "antd";
import { DownloadOutlined, PlusOutlined, SendOutlined, UserOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { collection, getDocs, updateDoc, doc, addDoc, query, where, getDoc, deleteDoc } from "firebase/firestore";
import { getStorage, ref, getDownloadURL, deleteObject } from "firebase/storage";
import { db } from "../firebase/firebase";
import { getAuth } from "firebase/auth";
import PatientDetailsModal from "./medecins/PatientDetailsModal";
import MedecinsTable from "./medecins/MedecinsTable";
import PatientsTable from "./medecins/PatientsTable";
import TPCheckModal from "./medecins/TPCheckModal";

const { Title, Text } = Typography;
const { Option } = Select;

const PACK_OPTIONS = [
  'Lite arcade unique',
  'Lite deux arcades',
  'Essential arcade unique',
  'Essential deux arcades',
  'Smart arcade unique',
  'Smart deux arcades',
  'Pro',
  'Pro +'
];

const PACK_PRICES = {
  "Lite arcade unique": 850,
  "Lite deux arcades": 1300,
  "Lite": 850,

  "Essential": 1150,
  "Essential deux arcades": 1950,
  "Essential arcade unique": 1150,

  "Smart": 2100,
  "Smart deux arcades": 3150,
  "Smart arcade unique": 2100,

  "Pro": 4500,
  "Pro +": 5000
};

const RHYTHM_OPTIONS = [7, 10, 14];

const Medecins = ({ currentUserRole }) => {
  const [medecins, setMedecins] = useState([]);
  const [patients, setPatients] = useState([]);
  const [showingPatients, setShowingPatients] = useState(false);
  const [currentMedecin, setCurrentMedecin] = useState(null);
  const [tpCheckModalVisible, setTpCheckModalVisible] = useState(false);
  const [alignersModalVisible, setAlignersModalVisible] = useState(false);
  const [currentPatient, setCurrentPatient] = useState(null);
  const [form] = Form.useForm();
  const [patientDetailsVisible, setPatientDetailsVisible] = useState(false);
  const [detailsType, setDetailsType] = useState(null);
  const [correctionModalVisible, setCorrectionModalVisible] = useState(false);
  const [selectedCorrection, setSelectedCorrection] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingMedecin, setEditingMedecin] = useState(null);
  const [editForm] = Form.useForm();
  const [viewMedecinModalVisible, setViewMedecinModalVisible] = useState(false);
  const [viewMedecin, setViewMedecin] = useState(null);
  const [addMedecinModalVisible, setAddMedecinModalVisible] = useState(false);
  const [addMedecinForm] = Form.useForm();
  const [addingMedecin, setAddingMedecin] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successAlignersModalVisible, setSuccessAlignersModalVisible] = useState(false);

  // Fetch medecins data
  useEffect(() => {
    const fetchMedecins = async () => {
      try {
        const medecinsSnapshot = await getDocs(collection(db, "medecins"));
        const medecinsList = medecinsSnapshot.docs.map((doc) => ({
          key: doc.id,
          ...doc.data(),
        }));
        setMedecins(medecinsList);
      } catch (error) {
        /*console.error("Error fetching medecins:", error);*/
      }
    };
    fetchMedecins();
  }, []);

  const showPatients = async (medecin) => {
    setCurrentMedecin(medecin);
    try {
      // Query patients where userId matches the current medecin's uid
      const patientsQuery = query(
        collection(db, "patients"),
        where("userId", "==", medecin.uid)
      );
      const patientsSnapshot = await getDocs(patientsQuery);
      const patientsList = patientsSnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          key: doc.id,
          ...data,
          correctionRequested: data.correctionRequested || false,
          correction: data.correction || "",
          correctionRequestDate: data.correctionRequestDate || null,
          archived: data.archived || 0,
          status: data.status || 0
        };
      });
      setPatients(patientsList);
      setShowingPatients(true);
    } catch (error) {
      /*console.error("Error fetching patients:", error);*/
    }
  };

  const showTPCheckModal = (patient) => {
    setCurrentPatient(patient);
    const initialBasePrice = patient.price || (patient.pack ? PACK_PRICES[patient.pack] || 0 : 0);
    const initialDiscount = patient.discount || 0;
    const initialFinalPrice = (initialDiscount && Number(initialDiscount) > 0)
      ? initialBasePrice - (initialBasePrice * (initialDiscount / 100))
      : initialBasePrice;
    const initialValues = {
      TPCheck: patient.TPCheck || "",
      alignersNumber: patient.alignersNumber || 0,
      upperArcadeSteps: patient.upperArcadeSteps || 0,
      lowerArcadeSteps: patient.lowerArcadeSteps || 0,
      changeRhythm: patient.changeRhythm || RHYTHM_OPTIONS[0],
      pack: patient.pack || "",
      price: initialBasePrice,
      discount: initialDiscount,
      finalPrice: initialFinalPrice
    };
    form.setFieldsValue(initialValues);
    setTpCheckModalVisible(true);
  };

  const handleTPCheckSubmit = async (values) => {
    try {
      const basePrice = values.price;
      const discount = values.discount;
      let discountedPrice = basePrice;
      if (discount && Number(discount) > 0) {
        discountedPrice = basePrice - (basePrice * (discount / 100));
      }
      let updateData = {
        TPCheck: values.TPCheck,
        alignersNumber: values.alignersNumber,
        upperArcadeSteps: values.upperArcadeSteps || 0,
        lowerArcadeSteps: values.lowerArcadeSteps || 0,
        changeRhythm: values.changeRhythm,
        pack: values.pack,
        price: basePrice, // base price at root
        discount: discount, // discount at root
        tpcheckDate: new Date().toISOString(),
        status: 2
      };
      const patientRef = doc(db, "patients", currentPatient.key);
      await updateDoc(patientRef, updateData);
      setPatients(prevPatients => 
        prevPatients.map(patient => 
          patient.key === currentPatient.key 
            ? { ...patient, ...updateData }
            : patient
        )
      );
      Modal.success({
        title: 'Success',
        content: 'TPCheck information updated successfully',
      });
      form.resetFields();
      setTpCheckModalVisible(false);
      const patientDoc = await getDoc(patientRef);
      if (patientDoc.exists()) {
        const updatedPatient = { ...patientDoc.data(), key: currentPatient.key };
        setCurrentPatient(updatedPatient);
      }
    } catch (error) {
      Modal.error({
        title: 'Error',
        content: 'Failed to update TPCheck information: ' + error.message,
      });
    }
  };

  const handleStatusChange = async (patientId, newStatus) => {
    try {
      const updateData = {};
      
      // If status is 6 (Archivé), set archived to 1
      if (newStatus === 6) {
        updateData.archived = 1;
      } else {
        updateData.status = newStatus;
        updateData.archived = 0; // Reset archived when changing to other status
      }
      
      await updateDoc(doc(db, "patients", patientId), updateData);
      
      // Update local state
      setPatients(patients.map(patient => 
        patient.key === patientId 
          ? { ...patient, ...updateData } 
          : patient
      ));
    } catch (error) {
      /*console.error("Error updating status:", error);*/
    }
  };

  const getStatusText = (status, archived, alignersShippedDate, alignersNumber) => {
    if (archived === 1) return "Archivé";
    if (status === 3 && alignersShippedDate) return "En production (Aligneurs Expédié)";
    const statusMap = {
      0: "Brouillon",
      1: "En planification",
      2: "En attente",
      3: "En production",
      4: "En traitement",
      5: "Terminé"
    };
    return statusMap[status] || "Inconnu";
  };
  
  // Download function using Firebase Storage's getDownloadURL
  const downloadFile = (url, filename) => {
    /*console.log(`Download requested for ${filename} from Firebase Storage`);*/
    
    if (!url) {
      Modal.error({ title: 'Error', content: 'No download URL provided' });
      return;
    }
    
    // Show loading message
    message.loading({ content: 'Preparing download...', key: 'download', duration: 0 });

    // Get file extension
    const fileExt = filename.split('.').pop().toLowerCase();
    
    try {
      // Extract the path from Firebase Storage URL
      const extractStoragePath = (url) => {
        try {
          // Handle Firebase Storage URL format
          if (url.includes('firebasestorage.googleapis.com')) {
            // Extract the path after /o/ and before ?
            const pathMatch = url.match(/\/o\/([^?]+)/);
            if (pathMatch && pathMatch[1]) {
              // Decode the URL-encoded path
              return decodeURIComponent(pathMatch[1]);
            }
          }
          // Return original URL if not a Firebase Storage URL or format not recognized
          return url;
        } catch (error) {
          /*console.error("Error extracting storage path:", error);*/
          return url;
        }
      };
      
      // Get storage instance
      const storage = getStorage();
      
      // If it's already a Firebase Storage URL, extract the path and get a fresh URL
      const storagePath = extractStoragePath(url);
      
      // If we couldn't extract a proper storage path, try direct download with the original URL
      if (storagePath === url) {
        /*console.log("Could not extract Firebase path, trying direct download");*/
        forceDownload(url, filename);
        return;
      }
      
      /*console.log("Extracted storage path:", storagePath);*/
      const storageRef = ref(storage, storagePath);
      
      // Get a fresh download URL (this handles authentication and CORS properly)
      getDownloadURL(storageRef)
        .then(downloadURL => {
          /*console.log("Got fresh download URL:", downloadURL);*/
          forceDownload(downloadURL, filename);
        })
        .catch(error => {
          /*console.error("Error getting download URL:", error);*/
          message.error({ content: 'Failed to get download URL', key: 'download', duration: 2 });
          
          // Try with the original URL as fallback
          /*console.log("Trying with original URL as fallback");*/
          forceDownload(url, filename);
        });
    } catch (error) {
      /*console.error("Error in download process:", error);*/
      message.error({ content: 'Download error', key: 'download', duration: 2 });
      
      // Try direct download as last resort
      forceDownload(url, filename);
    }
  };
  
  // Force download helper function
  const forceDownload = (url, filename) => {
    // Get file extension for content type detection
    const fileExt = filename.split('.').pop().toLowerCase();
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExt);
    const isStl = fileExt === 'stl';
    const isPly = fileExt === 'ply';
    const isPdf = fileExt === 'pdf';
    
    fetch(url)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        return response.blob();
      })
      .then(blob => {
        // Create object URL from blob
        const blobUrl = window.URL.createObjectURL(blob);
        
        // Create anchor element for download
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        
        // Force download for STL, PLY files and PDFs
        if (isStl || isPly || isPdf) {
          a.click();
          window.URL.revokeObjectURL(blobUrl);
          document.body.removeChild(a);
          message.success({ content: `${filename} download successful!`, key: 'download', duration: 2 });
        } 
        // For images, give option to view or download
        else if (isImage) {
          // Open in new tab but also trigger download
          window.open(blobUrl, '_blank');
          setTimeout(() => {
            a.click();
            window.URL.revokeObjectURL(blobUrl);
            document.body.removeChild(a);
            message.success({ content: `${filename} opened and downloading`, key: 'download', duration: 2 });
          }, 500);
        }
        // Default behavior for other file types
        else {
          a.click();
          window.URL.revokeObjectURL(blobUrl);
          document.body.removeChild(a);
          message.success({ content: `${filename} download successful!`, key: 'download', duration: 2 });
        }
      })
      .catch(error => {
        /*console.error("Download failed:", error);*/
        message.error({ content: 'Download failed, trying alternate method', key: 'download', duration: 2 });
        
        // Fallback method: directly open URL
        try {
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          a.click();
          
          message.info({ content: 'Attempting alternate download method', key: 'download', duration: 2 });
        } catch (e) {
          /*console.error("Fallback download also failed:", e);*/
          window.open(url, '_blank');
          message.info({ content: 'Opening in new tab instead', key: 'download', duration: 2 });
        }
      });
  };
  
  // Helper function to format teeth numbers
  const formatTeethNumbers = (teeth) => {
    if (!teeth || teeth.length === 0) return 'Aucune dent sélectionnée';
    return teeth.sort().join(', ');
  };

  // Helper function to format arcade text
  const formatArcadeText = (arcade, maxOption, mandOption) => {
    if (arcade === 'both') {
      return 'Les deux arcades';
    } else if (arcade === 'maxillaire') {
      return `Maxillaire${maxOption ? ` (Arcade opposée: ${
        maxOption === 'rien' ? 'Rien' : 
        maxOption === 'diagnostic' ? 'Diagnostic' : 
        maxOption === 'aligners' ? 'Aligners passifs' : ''
      })` : ''}`;
    } else if (arcade === 'mandibulaire') {
      return `Mandibulaire${mandOption ? ` (Arcade opposée: ${
        mandOption === 'rien' ? 'Rien' : 
        mandOption === 'diagnostic' ? 'Diagnostic' : 
        mandOption === 'aligners' ? 'Aligners passifs' : ''
      })` : ''}`;
    }
    return 'Non spécifié';
  };

  // Helper function to format rapport AP option
  const formatRapportAPOption = (option) => {
    switch (option) {
      case 'conserver': return 'Conserver la situation actuelle';
      case 'ameliorer_canine': return 'Améliorer la relation canine uniquement';
      case 'ameliorer_both': return 'Améliorer la relation canine & molaire';
      case 'correction': return 'Correction en Classe I';
      default: return option;
    }
  };

  // Helper function to format rapport AP
  const formatRapportAP = (rapportAP) => {
    if (!rapportAP) return 'Non spécifié';
    
    const text = [];

    if (rapportAP.D) {
      text.push(`Côté Droit: ${formatRapportAPOption(rapportAP.D)}`);
    }
    if (rapportAP.G) {
      text.push(`Côté Gauche: ${formatRapportAPOption(rapportAP.G)}`);
    }

    if (rapportAP.rip) {
      text.push(`RIP postérieure${rapportAP.ripPrecision ? ` (${rapportAP.ripPrecision})` : ''}`);
    }
    if (rapportAP.simulation) {
      text.push(`Simulation${rapportAP.simulationPrecision ? ` (${rapportAP.simulationPrecision})` : ''}`);
    }
    if (rapportAP.chirurgie) {
      text.push('Option chirurgicale');
    }

    return text.join('\n');
  };

  // Helper function to format espacement text
  const formatEspacementText = (prescriptionData) => {
    let text = [];

    // Add main espacement choice
    if (prescriptionData.espacement) {
      if (prescriptionData.espacement === 'fermer') {
        text.push("Fermer tous les espaces");
      } else if (prescriptionData.espacement === 'laisser') {
        text.push("Laisser des espaces spécifiques");
        if (prescriptionData.especesSpecifiques) {
          text.push(`Détails: ${prescriptionData.especesSpecifiques}`);
        }
      }
    }

    // Add maxillaire resolution details if any maxillaire data exists
    if (prescriptionData.expansion || prescriptionData.vestibuloversion || 
        prescriptionData.ripAnt || prescriptionData.ripPostDroite || prescriptionData.ripPostGauche) {
      text.push("\nRésolution au maxillaire:");
      if (prescriptionData.expansion) {
        text.push(`- Expansion: ${prescriptionData.expansion}`);
      }
      if (prescriptionData.vestibuloversion) {
        text.push(`- Vestibuloversion: ${prescriptionData.vestibuloversion}`);
      }
      if (prescriptionData.ripAnt) {
        text.push(`- RIP - Antérieure: ${prescriptionData.ripAnt}`);
      }
      if (prescriptionData.ripPostDroite) {
        text.push(`- RIP - Postérieure à droite: ${prescriptionData.ripPostDroite}`);
      }
      if (prescriptionData.ripPostGauche) {
        text.push(`- RIP - Postérieure à gauche: ${prescriptionData.ripPostGauche}`);
      }
    }

    // Add mandibulaire resolution details if any mandibulaire data exists
    if (prescriptionData.expansion2 || prescriptionData.vestibuloversion2 || 
        prescriptionData.ripAnt2 || prescriptionData.ripPostDroite2 || prescriptionData.ripPostGauche2) {
      text.push("\nRésolution à la mandibulaire:");
      if (prescriptionData.expansion2) {
        text.push(`- Expansion: ${prescriptionData.expansion2}`);
      }
      if (prescriptionData.vestibuloversion2) {
        text.push(`- Vestibuloversion: ${prescriptionData.vestibuloversion2}`);
      }
      if (prescriptionData.ripAnt2) {
        text.push(`- RIP - Antérieure: ${prescriptionData.ripAnt2}`);
      }
      if (prescriptionData.ripPostDroite2) {
        text.push(`- RIP - Postérieure à droite: ${prescriptionData.ripPostDroite2}`);
      }
      if (prescriptionData.ripPostGauche2) {
        text.push(`- RIP - Postérieure à gauche: ${prescriptionData.ripPostGauche2}`);
      }
    }

    // Add extractions information if present
    if (prescriptionData.extractions) {
      text.push("\nExtractions:");
      if (prescriptionData.extractions === 'none') {
        text.push("Aucune extraction");
      } else if (prescriptionData.extractions === 'some') {
        text.push("Extraire les dents suivantes:");
        if (prescriptionData.extractionsTeeth && prescriptionData.extractionsTeeth.length > 0) {
          text.push(`Dents sélectionnées: ${prescriptionData.extractionsTeeth.join(', ')}`);
        }
      }
    }

    return text.join('\n') || "Aucune information d'espacement spécifiée";
  };

  // Check if current medecin is orthodontiste
  const isOrthodontiste = currentMedecin?.role?.toLowerCase() === 'orthodontiste';
  
  const handleEditMedecin = (medecin) => {
    setEditingMedecin(medecin);
    editForm.setFieldsValue({
      name: medecin.name,
      email: medecin.email,
      role: medecin.role,
      phoneNumber: medecin.phoneNumber,
      city: medecin.city,
      country: medecin.country,
      medicalLicenseNumber: medecin.medicalLicenseNumber,
      reason: medecin.reason
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async (values) => {
    try {
      const medecinRef = doc(db, "medecins", editingMedecin.key);
      await updateDoc(medecinRef, values);
      
      // Update local state
      setMedecins(prevMedecins => 
        prevMedecins.map(medecin => 
          medecin.key === editingMedecin.key 
            ? { ...medecin, ...values }
            : medecin
        )
      );
      
      message.success('Medecin information updated successfully');
      setEditModalVisible(false);
      setEditingMedecin(null);
    } catch (error) {
      message.error('Failed to update medecin information');
    }
  };

  const handleDeleteMedecin = async (medecin) => {
    try {
      // 1. Delete the medecin's profile image from storage if it exists
      const storage = getStorage();
      const profileImageRef = ref(storage, `profile/${medecin.key}/profileimg`);
      try {
        await deleteObject(profileImageRef);
      } catch (error) {
        // Ignore error if file doesn't exist
        /*console.log("No profile image to delete");*/
      }

      // 2. Get all patients associated with this medecin
      const patientsQuery = query(
        collection(db, "patients"),
        where("userId", "==", medecin.uid)
      );
      const patientsSnapshot = await getDocs(patientsQuery);

      // 3. Delete all associated patients
      const deletePatientPromises = patientsSnapshot.docs.map(async (patientDoc) => {
        // Delete patient's images from storage if they exist
        const patientImages = patientDoc.data().images || {};
        const deleteImagePromises = Object.values(patientImages).map(async (imageUrl) => {
          try {
            const imageRef = ref(storage, imageUrl);
            await deleteObject(imageRef);
          } catch (error) {
            /*console.log("Error deleting patient image:", error);*/
          }
        });
        await Promise.all(deleteImagePromises);

        // Delete patient document
        return deleteDoc(doc(db, "patients", patientDoc.id));
      });
      await Promise.all(deletePatientPromises);

      // 4. Delete the medecin document
      await deleteDoc(doc(db, "medecins", medecin.key));

      // 5. Delete the user from Firebase Authentication
      const auth = getAuth();
      try {
        // Get the user by email
        const userCredential = await auth.fetchSignInMethodsForEmail(medecin.email);
        if (userCredential.length > 0) {
          // Get the user by UID
          const user = await auth.getUserByEmail(medecin.email);
          if (user) {
            await auth.deleteUser(user.uid);
          }
        }
      } catch (error) {
        /*console.error("Error deleting user from auth:", error);*/
        // Continue with the deletion process even if auth deletion fails
      }

      // 6. Update local state
      setMedecins(prevMedecins => prevMedecins.filter(m => m.key !== medecin.key));
      
      message.success('Medecin and associated data deleted successfully');
    } catch (error) {
      /*console.error("Error deleting medecin:", error);*/
      message.error('Failed to delete medecin');
    }
  };

  const showCorrectionDetails = (patient) => {
    setSelectedCorrection(patient);
    setCorrectionModalVisible(true);
  };

  const medecinColumns = [
    {
      title: "Addition Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => {
        if (!createdAt) return 'N/A';
        const dateObj = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
        return dateObj.toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Medecin Type",
      dataIndex: "role",
      key: "role",
      render: (role) => role || "N/A",
    },
    {
      title: "Phone",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      render: (phoneNumber) => phoneNumber || "N/A",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            icon={<UserOutlined />} 
            onClick={e => { e.stopPropagation(); showPatients(record); }}
          >
            Afficher Patients
          </Button>
          {currentUserRole === 'superadmin' && (
            <>
              <Button 
                type="default" 
                icon={<EditOutlined />} 
                onClick={e => { e.stopPropagation(); handleEditMedecin(record); }}
              >
                Edit
              </Button>
              <Popconfirm
                title="Delete Medecin"
                description="Are you sure you want to delete this medecin? This will also delete all associated patients and their data."
                onConfirm={() => handleDeleteMedecin(record)}
                okText="Yes"
                cancelText="No"
                okButtonProps={{ danger: true }}
              >
                <Button 
                  type="primary" 
                  danger 
                  icon={<DeleteOutlined />} 
                >
                  Delete
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  const patientColumns = [
    {
      title: "Addition Date",
      dataIndex: "additionDate",
      key: "additionDate",
      render: (_, record) => {
        const date = record.prescriptionSubmitedAt || record.createdAt;
        if (!date) return 'N/A';
        
        // Convert Firebase timestamp to Date object if needed
        const dateObj = date.toDate ? date.toDate() : new Date(date);
        
        // Format the date without time
        return dateObj.toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Surname",
      dataIndex: "surname",
      key: "surname",
    },
    {
      title: "Decision Medecin",
      dataIndex: "decisionStatus",
      key: "decisionStatus",
      render: (_, record) => {
        // Determine decision status
        let decisionStatus = "Pending";
        
        if (record.tpcheckValidated === true) {
          decisionStatus = "Validé";
        } else if (record.tpcheckValidated === false) {
          decisionStatus = "Non validé";
        } else if (record.correction) {
          decisionStatus = "Correction";
        }

        if (decisionStatus === "Validé") {
          return <span style={{ color: 'green' }}>{decisionStatus}</span>;
        } else if (decisionStatus === "Non validé") {
          return <span style={{ color: 'red' }}>{decisionStatus}</span>;
        } else if (decisionStatus === "Correction") {
          return (
            <span 
              style={{ color: 'orange', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={(e) => {
                e.stopPropagation(); // Prevent event from bubbling up
                showCorrectionDetails(record);
              }}
            >
              {decisionStatus}
            </span>
          );
        }
        return <span style={{ color: 'black' }}>{decisionStatus}</span>;
      }
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        const statusMap = {
          0: "Brouillon",
          1: "En planification",
          2: "En attente",
          3: "En production",
          4: "En traitement",
          5: "Terminé",
          6: "Archivé"
        };
        let statusText = statusMap[status] || "Inconnu";
        if (status === 3 && record.alignersShippedDate) {
          statusText = "En production (Aligneurs Expédié)";
        }
        if (currentUserRole === 'superadmin') {
          // If status is 3 and alignersShippedDate, show special label in Select
          const selectOptions = Object.entries(statusMap).map(([key, label]) => {
            if (parseInt(key, 10) === 3 && record.alignersShippedDate) {
              return <Select.Option key={key} value={3}>En production(aligners expedie)</Select.Option>;
            }
            return <Select.Option key={key} value={parseInt(key, 10)}>{label}</Select.Option>;
          });
          return (
            <Select
              value={record.archived === 1 ? 6 : (status === 3 && record.alignersShippedDate ? 3 : status)}
              style={{ width: '100%' }}
              onChange={value => handleStatusChange(record.key, value)}
              onClick={e => e.stopPropagation()}
            >
              {selectOptions}
            </Select>
          );
        } else {
          // Also update the plain text version for non-superadmin
          if (status === 3 && record.alignersShippedDate) {
            statusText = 'En production(aligners expedie)';
          }
          return <span>{statusText}</span>;
        }
      },
    },
    {
      title: "Eterna",
      dataIndex: "eterna",
      key: "eterna",
      render: (_, record) => {
        if (record.corrections) {
          return <span style={{ color: '#faad14', fontWeight: 'bold' }}>Refinement</span>;
        }  else if (record.eternaCommandDate) {
          return <span style={{ color: '#52c41a', fontWeight: 'bold' }}>✓</span>;
        }else if (record.traitementEndDate) {
          return <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>X</span>;
        }else {
          return <span style={{ color: '#8c8c8c' }}>-</span>;
        }
      },
    },
    {
      title: "Patient ID",
      dataIndex: "key",
      key: "key",
    },
  ];

  const showPatientDetails = (patient) => {
    setCurrentPatient(patient);
    setPatientDetailsVisible(true);
  };

  // Add this function to handle pack selection change
  const handlePackChange = (value) => {
    const price = PACK_PRICES[value] || 0;
    form.setFieldsValue({ price });
    // Calculate final price if discount exists
    const discount = form.getFieldValue('discount') || 0;
    const finalPrice = price - (price * (discount / 100));
    form.setFieldsValue({ finalPrice });
  };

  const handleDiscountChange = (value) => {
    const price = form.getFieldValue('price') || 0;
    const finalPrice = price - (price * (value / 100));
    form.setFieldsValue({ finalPrice });
  };

  return (
    <div>
      {!showingPatients && (
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0 }}>Medecins</Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddMedecinModalVisible(true)}>
            Add Medecin
          </Button>
        </div>
      )}
      {showingPatients ? (
        <>
          <div style={{ marginBottom: 16 }}>
            <Button onClick={() => setShowingPatients(false)}>
              Back to Medecins
            </Button>
            <Title level={4} style={{ marginTop: 16 }}>
              Patients of Dr. {currentMedecin.name}
            </Title>
          </div>
          <PatientsTable
            patients={patients.sort((a, b) => {
              const dateA = a.prescriptionSubmitedAt || a.createdAt;
              const dateB = b.prescriptionSubmitedAt || b.createdAt;
              if (!dateA) return 1;
              if (!dateB) return -1;
              const timeA = dateA.toDate ? dateA.toDate().getTime() : new Date(dateA).getTime();
              const timeB = dateB.toDate ? dateB.toDate().getTime() : new Date(dateB).getTime();
              return timeB - timeA; // Sort in descending order (newest first)
            })}
            columns={patientColumns}
            onRow={(record) => ({
              onClick: () => showPatientDetails(record),
              style: { cursor: 'pointer' }
            })}
          />
        </>
      ) : (
        <MedecinsTable
          medecins={medecins.slice().sort((a, b) => {
            const dateA = a.createdAt;
            const dateB = b.createdAt;
            if (!dateA) return 1;
            if (!dateB) return -1;
            const timeA = dateA.toDate ? dateA.toDate().getTime() : new Date(dateA).getTime();
            const timeB = dateB.toDate ? dateB.toDate().getTime() : new Date(dateB).getTime();
            return timeB - timeA; // Most recent first
          })}
          columns={medecinColumns}
          onRow={(record) => ({
            onClick: () => {
              setViewMedecin(record);
              setViewMedecinModalVisible(true);
            },
            style: { cursor: 'pointer' }
          })}
        />
      )}
      
      <TPCheckModal
        open={tpCheckModalVisible}
        onCancel={() => setTpCheckModalVisible(false)}
        onFinish={handleTPCheckSubmit}
        form={form}
        handlePackChange={handlePackChange}
        handleDiscountChange={handleDiscountChange}
        PACK_OPTIONS={PACK_OPTIONS}
        RHYTHM_OPTIONS={RHYTHM_OPTIONS}
      />
      
      <Modal
        title="Confirmer l'envoi des aligners"
        open={alignersModalVisible}
        onOk={async () => {
          try {
            // First update Firestore
            const patientRef = doc(db, "patients", currentPatient.key);
            await updateDoc(patientRef, {
              alignersShippedDate: new Date().toISOString()
            });

            // Finally refresh the data
            await showPatients(currentMedecin);
            setPatientDetailsVisible(false);
            setAlignersModalVisible(false);
            setTimeout(() => {
              setSuccessAlignersModalVisible(true);
            }, 300);
          } catch (error) {
            console.error('Error:', error);
            message.error('Erreur lors de la mise à jour');
          }
        }}
        onCancel={() => setAlignersModalVisible(false)}
        okText="Confirmer"
        cancelText="Annuler"
      >
        <p>Êtes-vous sûr de vouloir marquer les aligners comme envoyés pour ce patient ?</p>
      </Modal>
      
      <Modal
        title="Correction Details"
        open={correctionModalVisible}
        onCancel={() => {
          setCorrectionModalVisible(false);
          setSelectedCorrection(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setCorrectionModalVisible(false);
            setSelectedCorrection(null);
          }}>
            Close
          </Button>
        ]}
      >
        {selectedCorrection && (
          <div style={{ 
            padding: '12px', 
            backgroundColor: '#f5f5f5', 
            borderRadius: '4px',
            whiteSpace: 'pre-wrap'
          }}>
            {selectedCorrection.correction}
          </div>
        )}
      </Modal>
      
      <Modal
        title="Edit Medecin Information"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingMedecin(null);
          editForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please input the name!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please input the email!' },
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="role"
            label="Medecin Type"
            rules={[{ required: true, message: 'Please select the medecin type!' }]}
          >
            <Select>
              <Option value="orthodontiste">orthodontiste</Option>
              <Option value="dentiste omnipraticien">dentiste omnipraticien</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="phoneNumber"
            label="Phone Number"
            rules={[{ required: true, message: 'Please input the phone number!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="city"
            label="City"
            rules={[{ required: true, message: 'Please input the city!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="country"
            label="Country"
            rules={[{ required: true, message: 'Please input the country!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="medicalLicenseNumber"
            label="Medical License Number"
            rules={[]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="reason"
            label="Address"
            rules={[{ required: true, message: 'Please input the address!' }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Save Changes
              </Button>
              <Button onClick={() => {
                setEditModalVisible(false);
                setEditingMedecin(null);
                editForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      
      <PatientDetailsModal 
        visible={patientDetailsVisible}
        patient={currentPatient}
        onClose={() => setPatientDetailsVisible(false)}
        currentMedecin={currentMedecin}
        downloadFile={downloadFile}
        formatTeethNumbers={formatTeethNumbers}
        formatArcadeText={formatArcadeText}
        formatRapportAP={formatRapportAP}
        formatEspacementText={formatEspacementText}
        onSendAligners={() => {
          setPatientDetailsVisible(false);
          setAlignersModalVisible(true);
        }}
        onShowTPCheck={() => {
          setPatientDetailsVisible(false);
          setTimeout(() => {
            showTPCheckModal(currentPatient);
          }, 300);
        }}
      />
      
      {/* View Medecin Info Modal */}
      <Modal
        title="Medecin Information"
        open={viewMedecinModalVisible}
        onCancel={() => setViewMedecinModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewMedecinModalVisible(false)}>
            Close
          </Button>
        ]}
      >
        {viewMedecin && (
          <div style={{ padding: '8px 0' }}>
            <p><strong>Name:</strong> {viewMedecin.name}</p>
            <p><strong>Email:</strong> {viewMedecin.email}</p>
            <p><strong>Medecin Type:</strong> {viewMedecin.role}</p>
            <p><strong>Phone Number:</strong> {viewMedecin.phoneNumber}</p>
            <p><strong>City:</strong> {viewMedecin.city}</p>
            <p><strong>Country:</strong> {viewMedecin.country}</p>
            <p><strong>Medical License Number:</strong> {viewMedecin.medicalLicenseNumber}</p>
            <p><strong>Address:</strong> {viewMedecin.reason}</p>
          </div>
        )}
      </Modal>

      {/* Add Medecin Modal */}
      <Modal
        title="Add Medecin"
        open={addMedecinModalVisible}
        onCancel={() => {
          setAddMedecinModalVisible(false);
          addMedecinForm.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={addMedecinForm}
          layout="vertical"
          onFinish={async (values) => {
            setAddingMedecin(true);
            try {
              // Create a separate Firebase app instance for medecin creation (same as admin creation)
              const { initializeApp } = await import('firebase/app');
              const { initializeAuth, indexedDBLocalPersistence, createUserWithEmailAndPassword } = await import('firebase/auth');
              
              const medecinApp = initializeApp({
                apiKey: "AIzaSyA9POaFPDmFWLg2WiBtlZXU7sWlgbSG2E4",
                authDomain: "diamond-project-cf37b.firebaseapp.com",
                projectId: "diamond-project-cf37b",
                storageBucket: "diamond-project-cf37b.appspot.com",
                messagingSenderId: "381060633989",
                appId: "1:381060633989:web:98af578db08a986dc8a0ec",
                measurementId: "G-4VJB3Z508T"
              }, 'medecin-creation');
              
              const medecinAuth = initializeAuth(medecinApp, {
                persistence: indexedDBLocalPersistence
              }, 'medecin-auth');
              
              // Create auth user with the password from the form
              const userCredential = await createUserWithEmailAndPassword(
                medecinAuth, 
                values.email.toLowerCase(), 
                values.password
              );
              
              const userId = userCredential.user.uid;
              
              // Add to medecins collection with the userId
              const medecinDoc = await addDoc(collection(db, "medecins"), {
                name: values.name,
                email: values.email.toLowerCase(),
                role: values.role,
                phoneNumber: values.phoneNumber,
                city: values.city,
                country: values.country,
                medicalLicenseNumber: values.medicalLicenseNumber || "",
                reason: values.reason,
                uid: userId,
                createdAt: new Date().toISOString(),
              });
              
              // Update local state
              setMedecins((prev) => [
                ...prev,
                {
                  key: medecinDoc.id,
                  name: values.name,
                  email: values.email.toLowerCase(),
                  role: values.role,
                  phoneNumber: values.phoneNumber,
                  city: values.city,
                  country: values.country,
                  medicalLicenseNumber: values.medicalLicenseNumber,
                  reason: values.reason,
                  uid: userId,
                  createdAt: new Date().toISOString(),
                },
              ]);
              
              setSuccessModalVisible(true);
              setAddMedecinModalVisible(false);
              addMedecinForm.resetFields();
            } catch (error) {
              console.error("Error saving medecin:", error);
              
              let errorMsg = "Failed to save medecin";
              if (error.code === 'auth/email-already-in-use') {
                errorMsg = "This email is already registered. Please use a different email.";
              } else if (error.code === 'auth/weak-password') {
                errorMsg = "Password is too weak. Please use a stronger password (at least 6 characters).";
              } else {
                errorMsg = `Failed to save medecin: ${error.message}`;
              }
              
              setErrorMessage(errorMsg);
              setErrorModalVisible(true);
            } finally {
              setAddingMedecin(false);
            }
          }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please input the name!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please input the email!' },
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please input the password!' },
              { min: 6, message: 'Password must be at least 6 characters.' }
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="role"
            label="Medecin Type"
            rules={[{ required: true, message: 'Please select the medecin type!' }]}
          >
            <Select>
              <Option value="orthodontiste">orthodontiste</Option>
              <Option value="dentiste omnipraticien">dentiste omnipraticien</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="phoneNumber"
            label="Phone Number"
            rules={[{ required: true, message: 'Please input the phone number!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="city"
            label="City"
            rules={[{ required: true, message: 'Please input the city!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="country"
            label="Country"
            rules={[{ required: true, message: 'Please input the country!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="medicalLicenseNumber"
            label="Medical License Number"
            rules={[]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="reason"
            label="Address"
            rules={[{ required: true, message: 'Please input the address!' }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={addingMedecin}>
              Add Medecin
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Success Modal */}
      <Modal
        title="Success"
        open={successModalVisible}
        onOk={() => setSuccessModalVisible(false)}
        onCancel={() => setSuccessModalVisible(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setSuccessModalVisible(false)}>
            OK
          </Button>
        ]}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }}>✓</div>
          <p style={{ fontSize: '16px', margin: 0 }}>
            Medecin added successfully!
          </p>
          <p style={{ fontSize: '14px', color: '#666', margin: '8px 0 0 0' }}>
            They can now log in with their email and password.
          </p>
        </div>
      </Modal>

      {/* Error Modal */}
      <Modal
        title="Error"
        open={errorModalVisible}
        onOk={() => setErrorModalVisible(false)}
        onCancel={() => setErrorModalVisible(false)}
        footer={[
          <Button key="ok" type="primary" danger onClick={() => setErrorModalVisible(false)}>
            OK
          </Button>
        ]}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: '48px', color: '#ff4d4f', marginBottom: '16px' }}>✗</div>
          <p style={{ fontSize: '16px', margin: 0, color: '#ff4d4f' }}>
            {errorMessage}
          </p>
        </div>
      </Modal>

      {/* Custom Success Modal for Aligners */}
      <Modal
        title="Succès"
        open={successAlignersModalVisible}
        onOk={() => setSuccessAlignersModalVisible(false)}
        onCancel={() => setSuccessAlignersModalVisible(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setSuccessAlignersModalVisible(false)}>
            OK
          </Button>
        ]}
        centered
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }}>✓</div>
          <p style={{ fontSize: '16px', margin: 0 }}>
            Aligners expédié successfully
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default Medecins;